import { useEffect } from 'react';
import { useParams, useBlocker } from 'react-router-dom';
import { AxiosError } from 'axios';
import { EProjectStatus, EStatusCode } from '@/enums';
import ProjectShareLink from '@/components/projects/single-project/share/ProjectShareLink';
import ProjectSharePanel from '@/components/projects/single-project/share/ProjectSharePanel';
import EmbedCodeGenerator from '@/components/projects/single-project/share/EmbedCodeGenerator';
import useErrorStore from '@/store/ErrorStore';
import FullPageLoader from '@/components/atoms/Loader/FullPageLoader';
import ProjectShareTestLink from '@/components/projects/single-project/share/ProjectShareTestLink';
import UnsavedChangesModal from '@/components/Modals/UnsavedChangesModal';
import useCreateProjectStore from '@/store/CreateProjectStore';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import { getProjectTypeConfig, isEmbedProjectType } from '@/lib/projectTypeConfig';

function ProjectShare() {
  const { setShowError } = useErrorStore();
  const { isFormChanged } = useCreateProjectStore();
  const { project_id } = useParams();

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isFormChanged && currentLocation.pathname !== nextLocation.pathname,
  );

  const {
    currentProject: singleProjectData,
    error: projectDataError,
    isLoading,
  } = useCurrentProject();

  useEffect(() => {
    if ((projectDataError as AxiosError)?.status === EStatusCode.BAD_REQUEST) {
      setShowError(true);
    }
  }, [projectDataError, setShowError]);

  if (blocker.state === 'blocked') return <UnsavedChangesModal blocker={blocker} />;

  if (isLoading) return <FullPageLoader />;

  const { showPanel } = getProjectTypeConfig(singleProjectData?.project_type);
  const isEmbed = isEmbedProjectType(singleProjectData?.project_type);

  if (isEmbed) {
    const projectLink = `${import.meta.env.VITE_ENVIRONMENT_CHAT}/${project_id}`;
    return (
      <main className="flex flex-col w-full h-full mx-auto pt-5">
        <EmbedCodeGenerator
          projectLink={projectLink}
          projectInputMethods={singleProjectData?.type_of_interview?.filter(
            (m): m is string => !!m,
          )}
          projectStatus={singleProjectData?.status as EProjectStatus | undefined}
        />
      </main>
    );
  }

  return (
    <main className="flex flex-col w-full h-full mx-auto pt-5 gap-10">
      <ProjectShareTestLink singleProjectData={singleProjectData!} />
      <ProjectShareLink singleProjectData={singleProjectData!} />
      {showPanel && <ProjectSharePanel singleProjectData={singleProjectData!} />}
    </main>
  );
}

export default ProjectShare;
